import { User, UserPagination, EmployeesFilter } from '../../types';
import apiClient, { addParametersToString } from '../apiClient';

interface AssignEmployeesResponse {
    message: string;
}

interface TeamAccountEmployeesResponse {
    data: User[];
}

class TeamAccountEmployeeService {
    /**
     * Get assigned employees for a team account
     * GET /api/company/team-accounts/:id/employees
     */
    async getTeamAccountAssignedEmployees(teamAccountId: number): Promise<TeamAccountEmployeesResponse> {
        return (await apiClient.get<TeamAccountEmployeesResponse>(`/api/company/team-accounts/${teamAccountId}/employees`)).data;
    }

    /**
     * Get assigned employees for a team account with pagination
     * GET /api/company/team-accounts/:id/employees?page=1&perPage=20&search=term
     */
    async getTeamAccountAssignedEmployeesPaginated(teamAccountId: number, filter: EmployeesFilter): Promise<UserPagination> {
        return (
            await apiClient.get<UserPagination>(
                `/api/company/team-accounts/${teamAccountId}/employees${addParametersToString({
                    ...filter,
                    page: filter?.page ? filter.page : 1,
                    perPage: filter?.perPage ? filter.perPage : 20,
                    search: filter?.search ? filter.search : '',
                })}`
            )
        ).data;
    }

    /**
     * Assign employees to a team account (replaces existing)
     * PUT /api/company/team-accounts/:id/employees
     */
    async assignEmployeesToTeamAccount(teamAccountId: number, employeeIds: number[]): Promise<AssignEmployeesResponse> {
        return (
            await apiClient.put<AssignEmployeesResponse>(`/api/company/team-accounts/${teamAccountId}/employees`, {
                employeeIds,
            })
        ).data;
    }

    /**
     * Add employees to a team account (append)
     * POST /api/company/team-accounts/:id/employees
     */
    async addEmployeesToTeamAccount(teamAccountId: number, employeeIds: number[]): Promise<AssignEmployeesResponse> {
        return (
            await apiClient.post<AssignEmployeesResponse>(`/api/company/team-accounts/${teamAccountId}/employees`, {
                employeeIds,
            })
        ).data;
    }

    /**
     * Remove employees from a team account
     * DELETE /api/company/team-accounts/:id/employees
     */
    async removeEmployeesFromTeamAccount(teamAccountId: number, employeeIds: number[]): Promise<AssignEmployeesResponse> {
        return (
            await apiClient.delete<AssignEmployeesResponse>(`/api/company/team-accounts/${teamAccountId}/employees`, {
                data: { employeeIds },
            })
        ).data;
    }

    /**
     * Get team accounts assigned to an employee
     * GET /api/company/employees/:id/team-accounts
     */
    async getEmployeeAssignedTeamAccounts(employeeId: number): Promise<TeamAccountEmployeesResponse> {
        return (await apiClient.get<TeamAccountEmployeesResponse>(`/api/company/employees/${employeeId}/team-accounts`)).data;
    }
}

export default new TeamAccountEmployeeService();
